import { ChevronDown, Paperclip, Plus, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  Credential,
  CredentialDocument,
  CredentialVerification,
} from "../../types";
import { CredentialDetailsPopover } from "./credential-details-popover";
import { credentialRowSummary } from "./credential-details/credential-summaries";
import { CredentialSummaryLine } from "./credential-details/credential-summary-line";
import { CredentialStatusColumn } from "./credential-status-column";
import { ProviderImportAction } from "../provider-imports/provider-import-action";

/**
 * One instance of a credential — a single state licence, not the whole type.
 *
 * Each instance names the actual credential and shows its identifying facts.
 * The type header above keeps related holdings and their Add action together.
 *
 * Editing lives in the popover rather than on the row: a button per row made
 * the list mostly buttons, and the popover is already where the credential's
 * values are. A credential that does not exist yet keeps its own action here,
 * since there is nothing to open a popover on.
 *
 * `userId` is unused today but stays threaded through so it is already in place
 * for the provider-side mutations (which will need `switchUserId`).
 */
export function CredentialRow({
  credential,
  userId: _userId,
  readOnly = false,
  onEdit,
  onOpenDocument,
  onOpenVerificationEvidence,
  isOpeningDocument = false,
  onRemoveDocument,
  appearance = "default",
}: {
  credential: Credential;
  userId: string;
  appearance?: "default" | "panel";
  /** Hides write affordances entirely rather than disabling them: a disabled
   * control with no explanation reads as broken, and a reader who may not edit
   * is not looking at an unfinished feature. */
  readOnly?: boolean;
  onEdit?: (credential: Credential) => void;
  onOpenDocument?: (document: CredentialDocument) => void;
  onOpenVerificationEvidence?: (verification: CredentialVerification) => void;
  isOpeningDocument?: boolean;
  /** Takes a document off THIS credential — the row supplies the credential
   * because a document row alone does not know which one it belongs to. */
  onRemoveDocument?: (input: {
    credential: Credential;
    document: CredentialDocument;
  }) => void;
}) {
  const summary = credentialRowSummary(credential);
  const isNotApplicable = credential.requirement === "not-applicable";
  const hasVerification = !!credential.verifications?.length;

  return (
    <div
      className={cn(
        "flex flex-wrap items-start justify-between gap-x-4 gap-y-2",
        credential.present
          ? "rounded-xl border border-slate-200 bg-white px-3 py-3 transition-colors hover:border-primary/20 hover:bg-primary/5"
          : "py-2",
        appearance === "panel" &&
          credential.present &&
          "grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]",
      )}
    >
      <div className="min-w-0 flex-[1_1_14rem]">
        {summary ? (
          <CredentialDetailsPopover
            credential={credential}
            onEdit={readOnly ? undefined : onEdit}
            onOpenDocument={onOpenDocument}
            onOpenVerificationEvidence={onOpenVerificationEvidence}
            isOpeningDocument={isOpeningDocument}
            onRemoveDocument={
              onRemoveDocument && credential.credentialId
                ? (document) => onRemoveDocument({ credential, document })
                : undefined
            }
          >
            <button
              type="button"
              className="group -m-1 flex w-full min-w-0 cursor-pointer items-start gap-2 rounded-lg p-1 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
            >
              <CredentialSummaryLine summary={summary} />

              <span className="ml-auto flex shrink-0 items-center gap-2 pt-0.5 text-slate-400">
                {credential.documents.length > 0 && (
                  <span className="flex items-center gap-1 text-xs">
                    <Paperclip className="h-3 w-3" aria-hidden="true" />
                    {credential.documents.length}
                    <span className="sr-only">
                      {credential.documents.length === 1
                        ? "document attached"
                        : "documents attached"}
                    </span>
                  </span>
                )}
                {hasVerification && (
                  <span
                    className="flex items-center gap-1 text-xs"
                    title="Latest verification available"
                  >
                    <Shield className="h-3 w-3" aria-hidden="true" />
                    <span className="sr-only">
                      Latest verification available
                    </span>
                  </span>
                )}
                <ChevronDown
                  className="h-3.5 w-3.5 transition-colors group-hover:text-primary"
                  aria-hidden="true"
                />
              </span>
            </button>
          </CredentialDetailsPopover>
        ) : (
          !isNotApplicable &&
          !readOnly && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-xl border-slate-400 px-3 text-xs font-medium text-primary hover:border-primary/20 hover:bg-primary/5"
              disabled={!onEdit}
              title={onEdit ? undefined : "Credential updates are coming soon"}
              onClick={onEdit ? () => onEdit(credential) : undefined}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add
            </Button>
          )
        )}
        <ProviderImportAction credentialId={credential.credentialId} />
      </div>

      <div
        className={cn(
          "ml-auto",
          appearance === "panel" &&
            credential.present &&
            "ml-0 min-w-0 sm:ml-auto",
        )}
      >
        <CredentialStatusColumn
          credential={credential}
          appearance={appearance}
        />
      </div>
    </div>
  );
}
